import { z } from 'zod';
import { CONTENT_SCHEMA_VERSION } from './types.js';

export const markSchema: z.ZodType<{ type: string; attrs?: Record<string, unknown> | null }> = z.object({
  type: z.string().min(1).max(40),
  attrs: z.record(z.unknown()).nullish(),
});

/** Recursive ProseMirror node schema. Depth is capped to keep payloads sane. */
export const richNodeSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    type: z.string().min(1).max(40),
    attrs: z.record(z.unknown()).nullish(),
    content: z.array(richNodeSchema).optional(),
    marks: z.array(markSchema).optional(),
    text: z.string().optional(),
  }),
);

export const richDocSchema = z.object({
  type: z.literal('doc'),
  attrs: z.record(z.unknown()).nullish(),
  content: z.array(richNodeSchema).default([]),
});

export const sectionSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    id: z.string().min(1).max(64),
    title: z.string().max(300).default(''),
    anchor: z.string().max(120).default(''),
    numbered: z.boolean().default(true),
    guidance: z.string().max(4000).optional(),
    locked: z.boolean().optional(),
    internal: z.boolean().optional(),
    body: richDocSchema,
    children: z.array(sectionSchema).default([]),
  }),
);

/** Structural limits. Exceeding one is a validation error, never silent truncation. */
export const CONTENT_LIMITS = {
  /** Deepest subsection nesting, counted from 1 for a top-level section. */
  maxSectionDepth: 8,
  /** Deepest block nesting inside a section body (lists inside quotes inside cells). */
  maxNodeDepth: 30,
  /** Total nodes across the document, to bound serialiser and export work. */
  maxNodes: 60_000,
} as const;

const baseDocumentContentSchema = z.object({
  schemaVersion: z.literal(CONTENT_SCHEMA_VERSION).default(CONTENT_SCHEMA_VERSION),
  sections: z.array(sectionSchema).default([]),
});

/**
 * Walks untrusted content iteratively to check the structural limits.
 *
 * This runs *before* the recursive Zod schema: parsing a pathologically nested
 * payload recursively would blow the stack and surface as a 500 rather than a
 * validation error.
 */
export function findContentLimitProblem(value: unknown): string | null {
  if (value === null || typeof value !== 'object') return null;
  const root = value as { sections?: unknown };
  if (!Array.isArray(root.sections)) return null;

  let nodes = 0;

  const nodeStack: { node: unknown; depth: number }[] = [];
  const pushNode = (node: unknown, depth: number) => nodeStack.push({ node, depth });

  const sectionStack: { section: unknown; depth: number }[] = root.sections.map((section) => ({ section, depth: 1 }));

  while (sectionStack.length) {
    const { section, depth } = sectionStack.pop()!;
    if (depth > CONTENT_LIMITS.maxSectionDepth) {
      return `Sections may be nested at most ${CONTENT_LIMITS.maxSectionDepth} levels deep`;
    }
    nodes += 1;
    if (nodes > CONTENT_LIMITS.maxNodes) return `A document may contain at most ${CONTENT_LIMITS.maxNodes} elements`;
    if (!section || typeof section !== 'object') continue;

    const typed = section as { children?: unknown; body?: unknown };
    if (Array.isArray(typed.children)) {
      for (const child of typed.children) sectionStack.push({ section: child, depth: depth + 1 });
    }
    if (typed.body) pushNode(typed.body, 1);

    while (nodeStack.length) {
      const current = nodeStack.pop()!;
      if (current.depth > CONTENT_LIMITS.maxNodeDepth) {
        return `Content may be nested at most ${CONTENT_LIMITS.maxNodeDepth} levels deep`;
      }
      nodes += 1;
      if (nodes > CONTENT_LIMITS.maxNodes) return `A document may contain at most ${CONTENT_LIMITS.maxNodes} elements`;
      const node = current.node as { content?: unknown } | null;
      if (!node || typeof node !== 'object' || !Array.isArray(node.content)) continue;
      for (const child of node.content) pushNode(child, current.depth + 1);
    }
  }

  return null;
}

export const documentContentSchema = z
  .unknown()
  .superRefine((value, ctx) => {
    const problem = findContentLimitProblem(value);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem, path: ['sections'] });
  })
  .pipe(baseDocumentContentSchema);

export const frontMatterSchema = z
  .object({
    id: z.string().optional(),
    reference: z.string().optional(),
    title: z.string(),
    type: z.string().optional(),
    version: z.string().optional(),
    status: z.string().optional(),
    owner: z.string().optional(),
    approver: z.string().optional(),
    classification: z.string().optional(),
    effective_from: z.string().optional(),
    review_due: z.string().optional(),
    tags: z.array(z.string()).optional(),
  })
  .passthrough();
